import { useMemo } from 'react';
import { Marker } from 'react-map-gl';
import { clsx } from 'clsx';
import { motion } from 'framer-motion';
import type { Station, FuelType } from '@/types';
import { useFilters } from '@/context/FiltersContext';
import { formatPrice } from '@/utils/format';

interface StationMarkerProps {
  station: Station;
  allStations: Station[];
  onClick: () => void;
  isSelected?: boolean;
}

// Price tier colors - based on percentile within results
const PRICE_TIERS = {
  cheap: {
    bg: '#16A34A', // green-600
    hoverBg: '#15803D', // green-700
  },
  average: {
    bg: '#F59E0B', // amber-500
    hoverBg: '#D97706', // amber-600
  },
  expensive: {
    bg: '#DC2626', // red-600
    hoverBg: '#B91C1C', // red-700
  },
} as const;

type PriceTier = keyof typeof PRICE_TIERS;

// Calculate price tier based on percentile
function getPriceTier(price: number, allPrices: number[]): PriceTier {
  if (allPrices.length < 3) return 'average';

  const sorted = [...allPrices].sort((a, b) => a - b);
  const index = sorted.indexOf(price);
  const percentile = index / (sorted.length - 1);

  if (percentile <= 0.25) return 'cheap';
  if (percentile >= 0.75) return 'expensive';
  return 'average';
}

// Get all prices for a fuel type from stations
function getPricesForFuelType(stations: Station[], fuelType: FuelType): number[] {
  return stations
    .map((s) => s.prices[fuelType]?.price)
    .filter((p): p is number => typeof p === 'number');
}

export function StationMarker({
  station,
  allStations,
  onClick,
  isSelected = false,
}: StationMarkerProps) {
  const { primaryFuelType } = useFilters();

  const priceInfo = station.prices[primaryFuelType];
  const price = priceInfo?.price;

  // Calculate price tier
  const { tier, tierColors } = useMemo(() => {
    if (!price) return { tier: 'average' as PriceTier, tierColors: PRICE_TIERS.average };

    const allPrices = getPricesForFuelType(allStations, primaryFuelType);
    const calculatedTier = getPriceTier(price, allPrices);
    return { tier: calculatedTier, tierColors: PRICE_TIERS[calculatedTier] };
  }, [price, allStations, primaryFuelType]);

  // Don't show marker if station doesn't have the primary fuel type
  if (!price) {
    return null;
  }

  return (
    <Marker
      longitude={station.longitude}
      latitude={station.latitude}
      anchor="bottom"
      onClick={(e) => {
        e.originalEvent.stopPropagation();
        onClick();
      }}
    >
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: isSelected ? 1.15 : 1, opacity: 1 }}
        whileHover={{ scale: 1.1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className={clsx(
          'cursor-pointer relative',
          isSelected && 'z-10'
        )}
        role="button"
        tabIndex={0}
        aria-label={`${station.name}, ${formatPrice(price)} per litre`}
        data-track="station_select"
        data-station-id={station.id}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onClick();
          }
        }}
      >
        {/* Pin shape */}
        <div
          className="relative flex flex-col items-center"
          style={{ filter: `drop-shadow(0 2px 6px rgba(0,0,0,${isSelected ? 0.4 : 0.25}))` }}
        >
          {/* Price bubble */}
          <div
            className={clsx(
              'px-2.5 py-1.5 rounded-lg text-white font-bold whitespace-nowrap',
              'transition-colors duration-200',
              isSelected ? 'ring-2 ring-white ring-offset-2' : ''
            )}
            style={{
              backgroundColor: tierColors.bg,
              fontSize: isSelected ? '0.8rem' : '0.7rem',
            }}
          >
            {formatPrice(price)}
            {/* Tier indicator dot */}
            <span
              className={clsx(
                'absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full border-2 border-white',
                tier === 'cheap' && 'bg-green-400',
                tier === 'average' && 'bg-amber-400',
                tier === 'expensive' && 'bg-red-400'
              )}
            />
          </div>

          {/* Pin point */}
          <div
            className="w-0 h-0 border-l-[7px] border-r-[7px] border-t-[10px] border-l-transparent border-r-transparent"
            style={{ borderTopColor: tierColors.bg }}
          />

          {/* Brand name on hover/select */}
          {isSelected && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute -top-8 left-1/2 -translate-x-1/2 whitespace-nowrap"
            >
              <div className="bg-gray-900 text-white text-xs px-2 py-1 rounded shadow-lg">
                {station.brand}
              </div>
            </motion.div>
          )}
        </div>
      </motion.div>
    </Marker>
  );
}
